import type { Pin } from '__types__';
import { makeFileURL } from 'lib/s3/s3Helper';
import { sortBy, uniq } from 'lodash';
import { pinMediaKindOf } from 'v5/patterns/widgets/PinMedia/pinMediaKind';
import {
  enumFilter,
  type EnumFilter
} from 'v5/api/_common/query/filters/EnumFilter';
import type { MediaLinkKind } from 'v5/patterns/widgets/gallery-editor/mediaLink';

/** What a card offers besides opening the pin, as v1's pin label did per kind. */
export type PinOpenAction = 'download' | 'openLink';

/** A link to pin, as `createPin` takes it. */
export type PinLinkWrite = { url: string; pin_type: string };

/** The tag filter narrows the board only once it has something to match. */
function isActive(filter: EnumFilter<string> | null): boolean {
  if (filter === null) return false;
  return !enumFilter.isSelectableOperator(filter.operator)
    ? true
    : filter.value.length > 0;
}

type PinAddSummary =
  | { type: 'added'; count: number }
  | { type: 'failed'; count: number }
  | { type: 'partly'; added: number; failed: number };

export const pinboardUtils = {
  /**
   * The live pins in their order. Pins never reordered have no `order`: after the ordered ones,
   * oldest first.
   */
  ordered: (pins: Pin[]): Pin[] => {
    const rank = (pin: Pin) => {
      const at = pin.order ?? -1;
      return at === -1 ? Infinity : at;
    };
    return pins
      .filter(pin => !pin.archived)
      .sort((a, b) => rank(a) - rank(b) || a.id - b.id);
  },

  /** The id of a pin the server made, or null when what came back has none. */
  idOf: (created: unknown): number | null =>
    typeof created === 'object' &&
    created !== null &&
    'id' in created &&
    typeof created.id === 'number'
      ? created.id
      : null,

  /** How a batch of added files ended: all of them, some with the failures counted, or none. */
  addSummaryOf: (added: number, failed: number): PinAddSummary => {
    if (failed === 0) return { type: 'added', count: added };
    if (added === 0) return { type: 'failed', count: failed };
    return { type: 'partly', added, failed };
  },

  /** The pins with `ids` taking their places in that order, as a reorder saved would leave them. */
  withOrder: (pins: Pin[], ids: number[]): Pin[] =>
    pins.map(pin => {
      const at = ids.indexOf(pin.id);
      return at === -1 ? pin : { ...pin, order: at };
    }),

  tagNames: (pin: Pin): string[] => (pin.tags ?? []).map(tag => tag.tag),

  /** Every tag on the board once, as the filter offers them. */
  tagOptions: (pins: Pin[]): string[] =>
    sortBy(uniq(pins.flatMap(pinboardUtils.tagNames)), name =>
      name.toLowerCase()
    ),

  isFilterActive: isActive,

  matches: (pin: Pin, filter: EnumFilter<string> | null): boolean =>
    !isActive(filter) ||
    enumFilter.matchesArray(filter, pinboardUtils.tagNames(pin)),

  /** The name v1 showed under a pin; `null`: untitled. */
  nameOf: (pin: Pin): string | null => {
    if (pin.file) return pin.file.name || pin.pin_type || null;
    const host = (pin.url ?? '')
      .replace(/^(?:https?:\/\/)?(?:www\.)?/i, '')
      .split('/')[0];
    return host || null;
  },

  /** The file or the address the pin holds. */
  hrefOf: (pin: Pin): string | null => {
    // `Pin.file` is typed as the DOM's File; the store holds the file model
    const file: unknown = pin.file;
    const stored =
      typeof file === 'object' &&
      file !== null &&
      'id' in file &&
      typeof file.id === 'number' &&
      'url' in file &&
      typeof file.url === 'string'
        ? makeFileURL({ ...file, id: file.id, url: file.url })
        : '';
    return stored || pin.url || null;
  },

  /** The stored file's id, as `useLocalPreviews` keys its copies. */
  fileIdOf: (pin: Pin): string | null => {
    // `Pin.file` is typed as the DOM's File; the store holds the file model
    const file: unknown = pin.file;
    return typeof file === 'object' &&
      file !== null &&
      'id' in file &&
      typeof file.id === 'number'
      ? String(file.id)
      : null;
  },

  /** v1 offered pictures, videos and files for download, anything else as a link to open. */
  openActionOf: (pin: Pin): PinOpenAction => {
    switch (pinMediaKindOf(pin)) {
      case 'image':
      case 'file':
      case 'video':
        return 'download';
      default:
        return 'openLink';
    }
  },

  /**
   * The kind of file a pin's tile takes it for (a MIME type): its picture's or video's, a link
   * for a web page, else none (the file's name tells, a PDF or a document).
   */
  mimeTypeOf: (
    pin: Pin,
    mediaType: 'image' | 'video' | null
  ): string | undefined => {
    if (mediaType !== null) return `${mediaType}/*`;
    return pinboardUtils.openActionOf(pin) === 'openLink'
      ? 'text/uri-list'
      : undefined;
  },

  /** Only a picture can be the profile's picture, as in v1. */
  canBeProfilePicture: (pin: Pin): boolean => pinMediaKindOf(pin) === 'image',

  /**
   * A link to pin, with the pin type v1 gave it: a video of its provider, a picture (named by
   * its extension or loaded as one), or a web page.
   */
  linkWrite: (
    link: Exclude<MediaLinkKind, { type: 'notAUrl' }>,
    loadedAsPicture: boolean
  ): PinLinkWrite => {
    switch (link.type) {
      case 'video':
        return { url: link.url, pin_type: `${link.provider}/video` };
      case 'image':
        return { url: link.url, pin_type: 'external_image_link' };
      case 'unknown':
        return {
          url: link.url,
          pin_type: loadedAsPicture ? 'external_image_link' : 'external_link'
        };
    }
  }
};
